import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { 
  FaPlay, 
  FaStar, 
  FaUsers, 
  FaClock, 
  FaGraduationCap,
  FaCheckCircle,
  FaArrowRight,
  FaBook,
  FaCertificate,
  FaInfinity,
  FaMobile,
  FaDownload,
  FaShare,
  FaHeart,
  FaShoppingCart,
  FaTwitter,
  FaLinkedin
} from 'react-icons/fa';

const LXDCourseDetail = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);

  // Sample course data - in a real app, this would come from an API
  const course = {
    id: id,
    title: "Web Development Bootcamp",
    category: "Technology",
    level: "Intermediate",
    duration: "12 weeks",
    students: 1890,
    rating: 4.9,
    reviews: 456,
    price: "Birr 5,000",
    originalPrice: "Birr 8,000",
    image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800",
    instructor: {
      name: "Eng. Selamawit Bekele",
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200",
      bio: "Software engineer with 10+ years of experience at Google and Microsoft. Passionate about teaching web development.",
      courses: 15,
      students: 12500,
      rating: 4.9
    },
    description: "Master full-stack web development with our comprehensive bootcamp. Learn React, Node.js, MongoDB, and more while building real-world projects for the Ethiopian market.",
    whatYouLearn: [
      "Build modern web applications with React",
      "Create RESTful APIs with Node.js and Express",
      "Work with MongoDB databases",
      "Implement authentication and authorization",
      "Deploy applications to cloud platforms",
      "Follow best practices and industry standards"
    ],
    curriculum: [
      {
        week: 1,
        title: "Introduction to Web Development",
        lessons: [
          { title: "Course Overview", duration: "15 min", type: "video" },
          { title: "Setting Up Your Environment", duration: "30 min", type: "video" },
          { title: "HTML & CSS Basics", duration: "45 min", type: "video" },
          { title: "Quiz: HTML & CSS", duration: "20 min", type: "quiz" }
        ]
      },
      {
        week: 2,
        title: "JavaScript Fundamentals",
        lessons: [
          { title: "Variables and Data Types", duration: "40 min", type: "video" },
          { title: "Functions and Scope", duration: "35 min", type: "video" },
          { title: "DOM Manipulation", duration: "50 min", type: "video" },
          { title: "Project: Todo App", duration: "2 hours", type: "project" }
        ]
      },
      {
        week: 3,
        title: "React Essentials",
        lessons: [
          { title: "React Components", duration: "45 min", type: "video" },
          { title: "State and Props", duration: "40 min", type: "video" },
          { title: "Hooks Deep Dive", duration: "55 min", type: "video" },
          { title: "Quiz: React Basics", duration: "25 min", type: "quiz" }
        ]
      },
      {
        week: 4,
        title: "Node.js & Express",
        lessons: [
          { title: "Node.js Fundamentals", duration: "40 min", type: "video" },
          { title: "Express Framework", duration: "45 min", type: "video" },
          { title: "RESTful APIs", duration: "50 min", type: "video" },
          { title: "Project: API Development", duration: "3 hours", type: "project" }
        ]
      }
    ],
    requirements: [
      "Basic understanding of HTML and CSS",
      "Familiarity with programming concepts",
      "A computer with internet access",
      "Dedication to learn and practice"
    ],
    features: [
      { icon: <FaInfinity />, title: "Lifetime Access", description: "Access the course forever, including all future updates" },
      { icon: <FaMobile />, title: "Mobile Friendly", description: "Learn on any device, anywhere, anytime" },
      { icon: <FaCertificate />, title: "Certificate", description: "Earn a verified certificate upon completion" },
      { icon: <FaDownload />, title: "Downloadable Resources", description: "Access all course materials offline" }
    ]
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const handleEnroll = () => {
    setIsEnrolled(true);
  };

  const handleWishlist = () => {
    setIsWishlisted(!isWishlisted);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-purple-900 via-blue-900 to-cyan-900 text-white py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-12"
          >
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-purple-500/30 backdrop-blur px-4 py-2 rounded-full text-sm">
                  {course.category}
                </span>
                <span className="bg-blue-500/30 backdrop-blur px-4 py-2 rounded-full text-sm">
                  {course.level}
                </span>
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                {course.title}
              </h1>
              
              <p className="text-xl text-white/90 mb-6">
                {course.description}
              </p>
              
              <div className="flex flex-wrap items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <FaStar className="text-yellow-400" />
                  <span className="font-semibold">{course.rating}</span>
                  <span className="text-white/70">({course.reviews} reviews)</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaUsers className="text-purple-300" />
                  <span>{course.students.toLocaleString()} students</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaClock className="text-cyan-300" />
                  <span>{course.duration}</span>
                </div>
              </div>
            </div>

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 shadow-2xl"
            >
              <div className="relative mb-4">
                <img
                  src={course.image}
                  alt={course.title}
                  className="w-full h-48 object-cover rounded-xl"
                />
                <button className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl group hover:bg-black/50 transition-all">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <FaPlay className="text-purple-600 text-xl ml-1" />
                  </div>
                </button>
              </div>

              <div className="mb-4">
                {course.originalPrice && (
                  <div className="text-gray-400 line-through text-lg">{course.originalPrice}</div>
                )}
                <div className="text-3xl font-bold text-gray-800">{course.price}</div>
              </div>

              {!isEnrolled ? (
                <button
                  onClick={handleEnroll}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 rounded-xl font-semibold hover:opacity-90 transition-all mb-3 flex items-center justify-center gap-2"
                >
                  Enroll Now <FaArrowRight />
                </button>
              ) : (
                <button className="w-full bg-green-600 text-white py-4 rounded-xl font-semibold mb-3 flex items-center justify-center gap-2">
                  <FaCheckCircle /> Enrolled
                </button>
              )}

              <button
                onClick={handleWishlist}
                className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                  isWishlisted
                    ? 'bg-red-100 text-red-600 border-2 border-red-200'
                    : 'bg-gray-100 text-gray-700 border-2 border-gray-200 hover:bg-gray-200'
                }`}
              >
                <FaHeart className={isWishlisted ? 'text-red-600' : ''} />
                {isWishlisted ? 'Wishlisted' : 'Add to Wishlist'}
              </button>

              <div className="mt-6 space-y-3">
                {course.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 text-sm">
                    <div className="text-purple-600 mt-1">{feature.icon}</div>
                    <div>
                      <div className="font-semibold text-gray-800">{feature.title}</div>
                      <div className="text-gray-600">{feature.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            {/* Tabs */}
            <div className="flex gap-4 mb-8 border-b">
              {['overview', 'curriculum', 'instructor', 'reviews'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 font-semibold transition-all ${
                    activeTab === tab
                      ? 'text-purple-600 border-b-2 border-purple-600'
                      : 'text-gray-600 hover:text-purple-600'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              key={activeTab}
            >
              {activeTab === 'overview' && (
                <div className="space-y-8">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">What You'll Learn</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {course.whatYouLearn.map((item, index) => (
                        <motion.div
                          key={index}
                          variants={itemVariants}
                          className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm"
                        >
                          <FaCheckCircle className="text-green-500 mt-1 flex-shrink-0" />
                          <span className="text-gray-700">{item}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Requirements</h2>
                    <ul className="space-y-3">
                      {course.requirements.map((req, index) => (
                        <li key={index} className="flex items-start gap-3 text-gray-700">
                          <FaCheckCircle className="text-purple-600 mt-1 flex-shrink-0" />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Description</h2>
                    <p className="text-gray-700 leading-relaxed">
                      {course.description} This comprehensive bootcamp is designed to take you from beginner to professional web developer. 
                      You'll work on real projects that simulate actual business scenarios in the Ethiopian market, 
                      giving you practical experience that employers value.
                    </p>
                  </div>
                </div>
              )}

              {activeTab === 'curriculum' && (
                <div className="space-y-4">
                  {course.curriculum.map((week, index) => (
                    <motion.div
                      key={index}
                      variants={itemVariants}
                      className="bg-white rounded-2xl overflow-hidden shadow-sm"
                    >
                      <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4">
                        <h3 className="font-bold text-gray-800">Week {week.week}: {week.title}</h3>
                      </div>
                      <div className="divide-y">
                        {week.lessons.map((lesson, idx) => (
                          <Link 
                            key={idx} 
                            to={`/courses/${id}/lessons/${week.week}-${idx}`}
                            className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer block"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                {lesson.type === 'video' && <FaPlay className="text-purple-600 text-sm" />}
                                {lesson.type === 'quiz' && <FaBook className="text-purple-600 text-sm" />}
                                {lesson.type === 'project' && <FaGraduationCap className="text-purple-600 text-sm" />}
                              </div>
                              <span className="text-gray-800">{lesson.title}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-gray-500">{lesson.duration}</span>
                              <FaArrowRight className="text-purple-600" />
                            </div>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              {activeTab === 'instructor' && (
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <div className="flex items-start gap-6 mb-6">
                    <img
                      src={course.instructor.avatar}
                      alt={course.instructor.name}
                      className="w-24 h-24 rounded-full object-cover"
                    />
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800 mb-2">{course.instructor.name}</h2>
                      <p className="text-gray-600 mb-4">{course.instructor.bio}</p>
                      <div className="flex gap-6 text-sm">
                        <div className="flex items-center gap-2">
                          <FaGraduationCap className="text-purple-600" />
                          <span>{course.instructor.courses} Courses</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FaUsers className="text-purple-600" />
                          <span>{course.instructor.students.toLocaleString()} Students</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FaStar className="text-yellow-500" />
                          <span>{course.instructor.rating} Rating</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'reviews' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-2xl p-8 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-800">Student Reviews</h2>
                        <p className="text-gray-600">See what our students are saying</p>
                      </div>
                      <div className="text-right">
                        <div className="text-4xl font-bold text-gray-800">{course.rating}</div>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <FaStar key={i} className="text-yellow-500" />
                          ))}
                        </div>
                        <div className="text-sm text-gray-600">{course.reviews} reviews</div>
                      </div>
                    </div>
                  </div>

                  {[
                    { name: "Abebe Tesfaye", rating: 5, comment: "Excellent course! The instructor explains everything clearly. I got a job after completing this bootcamp.", date: "2 weeks ago" },
                    { name: "Selamawit Haile", rating: 5, comment: "Best investment I've made in my education. The projects were very practical and relevant to the Ethiopian market.", date: "1 month ago" },
                    { name: "Dawit Mekonnen", rating: 4, comment: "Great content and support. Would recommend to anyone looking to start a career in web development.", date: "2 months ago" }
                  ].map((review, index) => (
                    <motion.div
                      key={index}
                      variants={itemVariants}
                      className="bg-white rounded-2xl p-6 shadow-sm"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                            {review.name.charAt(0)}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-800">{review.name}</div>
                            <div className="text-sm text-gray-500">{review.date}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {[...Array(review.rating)].map((_, i) => (
                            <FaStar key={i} className="text-yellow-500 text-sm" />
                          ))}
                        </div>
                      </div>
                      <p className="text-gray-700">{review.comment}</p>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-bold text-gray-800 mb-4">Share this course</h3>
              <div className="flex gap-3">
                <button className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:opacity-90 transition-all">
                  <FaShare />
                </button>
                <button className="w-10 h-10 bg-sky-500 text-white rounded-full flex items-center justify-center hover:opacity-90 transition-all">
                  <FaTwitter />
                </button>
                <button className="w-10 h-10 bg-blue-700 text-white rounded-full flex items-center justify-center hover:opacity-90 transition-all">
                  <FaLinkedin />
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl p-6 text-white">
              <h3 className="font-bold mb-2">Need Help?</h3>
              <p className="text-white/90 text-sm mb-4">
                Our support team is available 24/7 to help you with any questions.
              </p>
              <Link
                to="/contact"
                className="inline-block bg-white text-purple-600 px-4 py-2 rounded-full font-semibold text-sm hover:bg-gray-100 transition-all"
              >
                Contact Support
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LXDCourseDetail;
